"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import {
  Home,
  BarChart3,
  Newspaper,
  LogIn,
  Menu,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, loadTokens } from "@/lib/api";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { useRegion } from "@/lib/regionContext";
import { useFxRate } from "@/lib/useFxRate";
import { formatBalanceForRegion, type MoneyCurrency } from "@/lib/format";
import { pickPrimaryCashWallet } from "@/lib/walletPicker";
import {
  DEFAULT_BADGE_LABEL,
  isBadgeActive,
  type FeatureBadge,
} from "@/lib/feature-badges";

/**
 * 2026-04-30 — Polymarket-parity mobile bottom nav.
 *
 * Fixed-bottom 4-tab bar shown only on mobile (md:hidden). Mirrors
 * the Polymarket pattern of Home / Search / Breaking / Balance.
 * Granbay-native: Home / Markets / Feed / Balance — the 4th tab is
 * a live BRL cash-balance pill that links to /home (same place
 * users go to manage their portfolio).
 *
 * Balance pill behaviour:
 *   - Authed: fetches once on mount + refreshes on the same
 *     granbay.balance.changed window event the rest of the app
 *     uses. Falls back to "—" while loading.
 *   - Anonymous: shows "Sign in" linking to /login.
 *
 * Hidden on desktop because SiteSidebar already covers nav there.
 *
 * Stacking note: MobileMarketBottomBar (sticky Buy YES/NO CTA on
 * /markets/[slug]) sits ABOVE this nav (z-40 vs z-30 here).
 */
const STATIC_TABS: ReadonlyArray<{
  href: string;
  labelKey: string;
  Icon: LucideIcon;
  /**
   * Optional feature badge — renders a small colored dot in the
   * top-right of the icon when active. The full pill won't fit a
   * 4-col bottom-nav cell, so we use a minimal dot indicator
   * instead. Same primitive as SiteSidebar.tsx; see
   * apps/web/lib/feature-badges.ts for the model.
   */
  badge?: FeatureBadge;
}> = [
  // Home tab points to / (the markets home with promo carousel +
  // browse panel), not /home (the user dashboard). Founder direction
  // 2026-05-24 — clicking Home from mobile should land on the public
  // home, not the private dashboard.
  { href: "/", labelKey: "mobileBottomNav.home", Icon: Home },
  { href: "/markets", labelKey: "mobileBottomNav.markets", Icon: BarChart3 },
  { href: "/feed", labelKey: "mobileBottomNav.feed", Icon: Newspaper },
];

export function MobileBottomNav() {
  const pathname = usePathname();
  const displayCurrency = useDisplayCurrency();
  const region = useRegion();
  const fx = useFxRate("BRL");
  const usdcPerBrl = fx?.rate ?? null;
  const { t } = useTranslation();
  // Three-state auth (mirrors SiteHeader): undefined = not yet
  // computed (pre-effect), false = anonymous, true = authed. The
  // 4th-slot render branches on this so we never flash "SIGN IN"
  // for a logged-in user mid-hydration (founder bug 2026-05-02 —
  // header pill correctly read tokens but the bottom nav was stuck
  // on the false-default until the effect's setAuthed re-rendered).
  const [authed, setAuthed] = useState<boolean | undefined>(undefined);
  const [balanceCents, setBalanceCents] = useState<number | null>(null);
  const [walletCurrency, setWalletCurrency] = useState<MoneyCurrency | null>(
    null,
  );

  useEffect(() => {
    const compute = () => {
       
      setAuthed(loadTokens() !== null);
    };
    compute();
    // Storage event covers logout / login from another tab so the
    // bottom nav doesn't stay stuck on the prior session's branch.
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === "granbay.tokens") compute();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    const load = async () => {
      try {
        const wallets = await api.listMyWallets();
        if (cancelled) return;
        // pickPrimaryCashWallet keeps desktop pill + mobile cell
        // anchored to the same wallet — the bug pre-extraction was
        // that two inline copies of this chain could drift on a
        // future edit.
        const cash = pickPrimaryCashWallet(wallets, displayCurrency);
        setBalanceCents(cash?.cents ?? 0);
        setWalletCurrency(cash?.currency ?? null);
      } catch {
        // soft-fail — pill renders "—"
      }
    };
    void load();
    const onChange = () => void load();
    window.addEventListener("granbay.balance.changed", onChange);
    return () => {
      cancelled = true;
      window.removeEventListener("granbay.balance.changed", onChange);
    };
  }, [authed, displayCurrency]);

  if (
    pathname === "/login" ||
    pathname === "/signup" ||
    pathname?.startsWith("/onboarding") ||
    // v2 redesign (2026-05-21) — v2 ships its own chrome; suppress
    // the v1 mobile bottom nav on every /v2/* route. Same for /v3.
    pathname?.startsWith("/v2") ||
    pathname?.startsWith("/v3") ||
    pathname?.startsWith("/v4") ||
    pathname?.startsWith("/v5")
    // /v6 used to suppress here when it was a demo route with its own
    // V6MobileBottomNav. With v6 promoted to localhost main theme
    // 2026-05-24, /v6 inherits this global nav like every other route.
  ) {
    return null;
  }

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-[var(--color-border)] bg-[var(--color-surface)] md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      aria-label={t("mobileBottomNav.ariaLabel")}
    >
      {STATIC_TABS.map((tab) => {
        // "/" must use exact-match (otherwise startsWith("/") is true
        // for every route and Home would always glow active).
        const active =
          tab.href === "/"
            ? pathname === "/"
            : pathname === tab.href ||
              pathname?.startsWith(tab.href);
        const showBadgeDot = isBadgeActive(tab.badge);
        const Icon = tab.Icon;
        const inner = (
          <>
            <span className="relative inline-flex">
              <Icon
                size={18}
                strokeWidth={1.5}
                aria-hidden="true"
                className="block"
              />
              {showBadgeDot && tab.badge && (
                <span
                  aria-label={t(
                    `featureBadges.${tab.badge.kind}`,
                    DEFAULT_BADGE_LABEL[tab.badge.kind],
                  )}
                  className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-[var(--color-brand)]"
                />
              )}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.4px]">
              {t(tab.labelKey)}
            </span>
          </>
        );

        // Markets tab opens the Polymarket-style mobile search sheet
        // (with category squares) instead of routing to /markets —
        // founder direction 2026-05-24. The sheet itself links to
        // /markets/?category=... when the user picks a square.
        if (tab.href === "/markets") {
          return (
            <button
              key={tab.href}
              type="button"
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent("granbay.mobilesearch.open"),
                )
              }
              aria-label={t(tab.labelKey)}
              className="flex flex-col items-center justify-center gap-0.5 py-2 transition-colors"
              style={{
                color: active ? "var(--color-brand)" : "var(--color-text3)",
              }}
            >
              {inner}
            </button>
          );
        }

        return (
          <Link
            key={tab.href}
            href={tab.href}
            aria-current={active ? "page" : undefined}
            className="flex flex-col items-center justify-center gap-0.5 py-2 transition-colors"
            style={{
              color: active ? "var(--color-brand)" : "var(--color-text3)",
            }}
          >
            {inner}
          </Link>
        );
      })}
      {/* 4th slot: live cash balance for authed users; sign-in CTA
          for anonymous viewers. While auth is still resolving (the
          initial undefined state pre-effect) we render an empty
          placeholder of the same height so the nav layout doesn't
          shift AND we don't flash "SIGN IN" for users who are
          actually logged in. */}
      {authed === undefined ? (
        <div
          aria-hidden
          className="flex flex-col items-center justify-center gap-0.5 py-2"
        />
      ) : authed ? (
        <MobileBalanceCell
          cents={balanceCents}
          walletCurrency={walletCurrency}
          displayCurrency={displayCurrency}
          usdcPerBrl={usdcPerBrl}
          region={region.region}
          balanceLabel={t("mobileBottomNav.balance")}
          balanceAria={t("mobileBottomNav.balanceAria")}
        />
      ) : (
        <Link
          href="/login"
          className="flex flex-col items-center justify-center gap-0.5 py-2 transition-colors"
          style={{ color: "var(--color-brand)" }}
        >
          <LogIn size={18} strokeWidth={1.5} aria-hidden="true" className="block" />
          <span className="text-[10px] font-bold uppercase tracking-[0.4px]">
            {t("mobileBottomNav.signIn")}
          </span>
        </Link>
      )}
      {/* 5th slot: hamburger that opens MobileSidebarDrawer. Moved
          from the top SiteHeader 2026-05-24 (Facebook pattern). Fires
          granbay.sidebar.open; SiteHeader listens + toggles its
          drawer instance. */}
      <button
        type="button"
        onClick={() =>
          window.dispatchEvent(new CustomEvent("granbay.sidebar.open"))
        }
        aria-label={t("header.openMenu")}
        className="flex flex-col items-center justify-center gap-0.5 py-2 transition-colors"
        style={{ color: "var(--color-text3)" }}
      >
        <Menu size={18} strokeWidth={1.5} aria-hidden="true" className="block" />
        <span className="text-[10px] font-bold uppercase tracking-[0.4px]">
          {t("mobileBottomNav.menu", { defaultValue: "Menu" })}
        </span>
      </button>
    </nav>
  );
}

/**
 * Mobile bottom-nav balance cell. Renders the locked dual-display
 * rule in a 2-line stack tuned for the ~90px mobile cell:
 *   line 1 — primary leg (wallet currency, USDC by default).
 *   line 2 — secondary leg "(≈ R$Y)" when the projection differs;
 *            falls back to the "Balance / Saldo" label otherwise.
 *
 * Replacing the static label with the projection on cross-currency
 * displays surfaces the locked rule without burning extra real
 * estate; on same-currency / rate-unavailable paths the label
 * stands in unchanged so the cell never goes blank.
 */
function MobileBalanceCell({
  cents,
  walletCurrency,
  displayCurrency,
  usdcPerBrl,
  region,
  balanceLabel,
  balanceAria,
}: {
  cents: number | null;
  walletCurrency: MoneyCurrency | null;
  displayCurrency: MoneyCurrency;
  usdcPerBrl: number | null;
  region: string;
  balanceLabel: string;
  balanceAria: string;
}) {
  // 2026-05-05 — region-aware. Founder report 2026-05-05: a US-flag
  // viewer with a legacy BRL wallet was seeing "R$… (≈ $…)" in the
  // mobile bottom-nav cell. formatBalanceForRegion (same helper /home
  // and the chrome pill use) suppresses R$ for non-BR users.
  const dual =
    cents === null
      ? null
      : formatBalanceForRegion(
          cents,
          walletCurrency,
          displayCurrency,
          usdcPerBrl,
          region,
          { compact: true },
        );
  return (
    <button
      type="button"
      onClick={() =>
        window.dispatchEvent(
          new CustomEvent("granbay.quickdashboard.open"),
        )
      }
      aria-label={balanceAria}
      className="flex flex-col items-center justify-center gap-0.5 py-2 transition-colors"
      style={{ color: "var(--color-text2)", background: "transparent", border: "none", cursor: "pointer" }}
    >
      <span className="text-[14px] font-mono font-bold tabular-nums leading-none">
        {dual === null ? "—" : dual.primary}
      </span>
      <span className="text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
        {dual !== null && dual.secondary !== null
          ? `≈ ${dual.secondary}`
          : balanceLabel}
      </span>
    </button>
  );
}
